/**
 * MCP Info Page — public-facing developer landing for the Inverio MCP server.
 *
 * Reachable at:
 *   - inverio.ai/mcp        (canonical)
 *   - mcp.inverio.ai/mcp    (after / redirect from vercel.json)
 *
 * The tool list is fetched live from the MCP discovery endpoint so it stays
 * in sync without manual edits. Falls back to a hardcoded list if the fetch
 * fails (offline, server outage, etc.).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Platform,
  ScrollView,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';
import * as Clipboard from 'expo-clipboard';
import {
  Terminal,
  Copy,
  Check,
  ExternalLink,
  Key,
  Zap,
  Shield,
  Code,
  ArrowRight,
  ChevronRight,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeIn } from 'react-native-reanimated';
import { PublicLayout } from '@/components/PublicLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { hapticLight, hapticSuccess } from '@/lib/haptics';
import { useTrackScreen } from '@/lib/analytics';
import { useIsAuthenticated } from '@/lib/supabase/auth';

const MCP_ENDPOINT = 'https://mcp.inverio.ai/server';
const MCP_PROTOCOL_VERSION = '2025-03-26';

/** Dark beta palette — must match PublicLayout's web background. */
const C = {
  surface: 'rgba(255,255,255,0.05)',
  surfaceElevated: 'rgba(255,255,255,0.08)',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.15)',
  text: '#FFFFFF',
  text1: 'rgba(255,255,255,0.85)',
  text2: 'rgba(255,255,255,0.6)',
  text3: 'rgba(255,255,255,0.38)',
  brand: '#4fd1c5',
  brandSoft: 'rgba(79,209,197,0.16)',
  green: '#34D399',
  greenSoft: 'rgba(52,211,153,0.18)',
  amber: '#FF9F0A',
  amberSoft: 'rgba(255,159,10,0.14)',
  codeBg: '#0a1628',
};

// ---------------------------------------------------------------------------
// Types & fallback data
// ---------------------------------------------------------------------------

interface DiscoveryTool {
  name: string;
  description: string;
  /** "When to use" hint — display-only, not part of the MCP discovery shape. */
  usage?: string;
}

const TOOLS: DiscoveryTool[] = [
  {
    name: 'score_startup',
    description: 'Evaluate a startup across 15 dimensions using the Inverio methodology.',
    usage: 'Call when you need a fresh score or rescore after evidence changes. Side effects.',
  },
  {
    name: 'verify_claim',
    description: 'Verify startup claims against external sources — registries, web, public data.',
    usage: 'Run after scoring to ground evidence and produce a verification report. Side effects.',
  },
  {
    name: 'check_dataroom',
    description: 'Document completeness and deal readiness for a startup data room.',
    usage: 'Read-only readiness snapshot — required vs present vs missing documents.',
  },
  {
    name: 'get_score_feedback',
    description: 'Scoring feedback with improvement guidance per dimension.',
    usage: 'Read-only — surfaces actionable next steps for the founder/investor.',
  },
  {
    name: 'sync_dataroom',
    description: 'Trigger a delta-aware resync of a connected data room.',
    usage: 'Fires when a connected drive (Drive/Dropbox) has new or changed files. Side effects.',
  },
  {
    name: 'get_dealflow',
    description: 'Investor dealflow pipeline with status, score, sector, stage filters.',
    usage: 'Read-only — paginate the investor pipeline with optional filters.',
  },
  {
    name: 'get_portfolio',
    description: 'Portfolio companies marked as invested with latest scores.',
    usage: 'Read-only — scoped to the authenticated investor.',
  },
  {
    name: 'get_startup_detail',
    description: 'Full startup profile with score, verification, and checklist summary.',
    usage: 'Read-only — best for "tell me everything about startup X" prompts.',
  },
  {
    name: 'get_daily_briefing',
    description: 'Aggregated briefing — new submissions, score changes, portfolio alerts.',
    usage: 'Read-only — designed for morning-briefing agent loops.',
  },
];

interface ResourceListItem {
  uri: string;
  name: string;
  description: string;
  scope: 'public' | 'user' | 'per-startup';
}

const RESOURCES: ResourceListItem[] = [
  {
    uri: 'inverio://methodology/overview',
    name: 'Methodology Overview',
    description: 'Public — 15 dimensions, Inverio IRS (5-band venture label: critical → weak → moderate → strong → excellent), evidence grade (low / moderate / high), deal readiness (not-ready / developing / nearly-ready / ready), and DD framework.',
    scope: 'public',
  },
  {
    uri: 'inverio://user/me/portfolio',
    name: 'My Portfolio',
    description: 'Your invested companies (dealflow rows with status=invested).',
    scope: 'user',
  },
  {
    uri: 'inverio://user/me/thesis',
    name: 'My Investment Thesis',
    description: 'Your active thesis — archetype and dimension weights.',
    scope: 'user',
  },
  {
    uri: 'inverio://user/me/dealflow/recent',
    name: 'Recent Dealflow (30d)',
    description: 'Recently added dealflow excluding already-invested rows.',
    scope: 'user',
  },
  {
    uri: 'inverio://startup/{id}/profile',
    name: 'Startup Profile (template)',
    description: 'Profile for any startup in your dealflow — name, industry, stage, problem/solution, metrics.',
    scope: 'per-startup',
  },
  {
    uri: 'inverio://startup/{id}/score-summary',
    name: 'Startup Score Summary (template)',
    description: 'Latest score for a startup — Inverio IRS, venture label, evidence grade, deal readiness, dimensions, risks, methodology version.',
    scope: 'per-startup',
  },
  {
    uri: 'inverio://startup/{id}/dd-checklist',
    name: 'Startup DD Checklist (template)',
    description: 'Document categories present in the data room — DD readiness signal.',
    scope: 'per-startup',
  },
  {
    uri: 'inverio://startup/{id}/verification-report',
    name: 'Startup Verification Report (template)',
    description: 'Latest claim verification — confirmed / contradicted / unverifiable, with sources.',
    scope: 'per-startup',
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    await Clipboard.setStringAsync(value);
    hapticSuccess();
    setCopied(true);
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => setCopied(false), 2200);
  }, [value]);

  return (
    <AnimatedPressable
      onPress={handleCopy}
      className="flex-row items-center justify-center gap-1.5 px-3 py-2 rounded-md"
      style={{
        backgroundColor: copied ? C.greenSoft : C.surfaceElevated,
        minWidth: label ? 90 : 36,
        borderWidth: 1,
        borderColor: copied ? C.green : C.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={copied ? 'Copied' : `Copy ${label ?? 'value'}`}
    >
      {copied ? <Check size={14} color={C.green} /> : <Copy size={14} color={C.text2} />}
      {label ? (
        <Text
          style={{ color: copied ? C.green : C.text1, fontFamily: 'monospace' }}
          className="text-xs font-medium"
          numberOfLines={1}
        >
          {copied ? 'Copied' : label}
        </Text>
      ) : null}
    </AnimatedPressable>
  );
}

function ConfigSnippet({
  client,
  config,
}: {
  client: string;
  config: Record<string, unknown>;
}) {
  const json = JSON.stringify(config, null, 2);
  return (
    <View
      className="rounded-xl mt-3 overflow-hidden"
      style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
    >
      <View
        className="flex-row items-center justify-between px-4 py-2.5"
        style={{ borderBottomWidth: 1, borderBottomColor: C.border }}
      >
        <View className="flex-row items-center gap-2">
          <Terminal size={13} color={C.text2} />
          <Text style={{ color: C.text }} className="text-sm font-semibold" numberOfLines={1}>
            {client}
          </Text>
        </View>
        <CopyButton value={json} label="Copy" />
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ padding: 14 }}
        // `flexGrow: 0` — a horizontal ScrollView expands vertically to fill a
        // flex parent otherwise, so this code block would claim the rest of the
        // card instead of hugging its content. 40 of the 41 horizontal
        // scrollers in the app set this; that made it the only one that did not.
        style={{ backgroundColor: C.codeBg, flexGrow: 0 }}
      >
        <Text
          style={{ color: '#E6FCFC', fontFamily: 'monospace' }}
          className="text-xs leading-5"
          selectable
        >
          {json}
        </Text>
      </ScrollView>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function McpInfoPage() {
  useTrackScreen('McpInfo');
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const { isAuthenticated, isLoading: authLoading } = useIsAuthenticated();

  // Auth gate — MCP info is private. Redirect anonymous users to login.
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.replace('/login');
    }
  }, [authLoading, isAuthenticated, router]);

  // Inject OG meta tags (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const og: [string, string][] = [
      ['og:type', 'website'],
      ['og:url', 'https://mcp.inverio.ai/'],
      ['og:title', 'Inverio MCP — Startup evaluation and due diligence for AI agents'],
      [
        'og:description',
        'The Inverio MCP server — Inverio IRS scoring, evidence-graded verification, and deal readiness for AI agents working with paid investors.',
      ],
      ['og:image', 'https://inverio.ai/og-image.png'],
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', 'Inverio MCP — evaluation + DD for AI agents'],
      ['twitter:description', '8 resources + 9 tools to score, verify, and conduct DD on startups via the Model Context Protocol.'],
      ['twitter:image', 'https://inverio.ai/og-image.png'],
    ];
    const created: HTMLMetaElement[] = [];
    og.forEach(([prop, content]) => {
      const m = document.createElement('meta');
      m.setAttribute(prop.startsWith('og:') ? 'property' : 'name', prop);
      m.setAttribute('content', content);
      m.id = `mcp-og-${prop}`;
      document.head.appendChild(m);
      created.push(m);
    });
    return () => created.forEach((m) => m.remove());
  }, []);

  // Tool list is hardcoded (not live-fetched) — discovery is now gated behind
  // API key auth, and we don't have a key on the info page itself. The list
  // ships with the codebase so it's always in sync with the deployed server.
  const tools = TOOLS;
  const protocolVersion = MCP_PROTOCOL_VERSION;

  const baseRemoteConfig = {
    url: MCP_ENDPOINT,
    headers: { Authorization: 'Bearer <your-api-key>' },
  };
  const claudeDesktopConfig = { mcpServers: { inverio: baseRemoteConfig } };
  const claudeWebConfig = {
    mcpServers: {
      inverio: {
        url: MCP_ENDPOINT,
        headers: { Authorization: 'Bearer <your-api-key>' },
      },
    },
  };
  const chatgptConfig = {
    mcpServers: {
      inverio: {
        url: MCP_ENDPOINT,
        headers: { Authorization: 'Bearer <your-api-key>' },
      },
    },
  };
  const cursorConfig = { mcpServers: { inverio: baseRemoteConfig } };
  const clineConfig = {
    mcpServers: {
      inverio: { ...baseRemoteConfig, type: 'http' },
    },
  };
  const windsurfConfig = {
    mcpServers: {
      inverio: {
        serverUrl: MCP_ENDPOINT,
        headers: { Authorization: 'Bearer <your-api-key>' },
      },
    },
  };

  // While auth is resolving or redirecting, show a minimal splash to avoid
  // flashing the page contents to anonymous users.
  if (authLoading || !isAuthenticated) {
    return (
      <View
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: '#0a1628' }}
      >
        <ActivityIndicator color={C.brand} />
      </View>
    );
  }

  return (
    <PublicLayout
      title="Inverio MCP"
      titleIcon={<Code size={22} color={C.text} />}
      maxWidth={920}
    >
      <Head>
        <title>Inverio MCP — Startup Evaluation Tools for AI Agents</title>
        <meta
          name="description"
          content="8 resources + 9 tools for AI agents to score, verify, and conduct due diligence on startups. Streamable HTTP, OAuth-ready."
        />
      </Head>

      <View style={{ paddingTop: isDesktop ? 24 : 8, paddingBottom: 64 }}>
        {/* Hero */}
        <Animated.View entering={FadeInDown.duration(400)}>
          <View className="flex-row items-center gap-2 mb-4">
            <View
              className="px-2.5 py-1 rounded-full flex-row items-center gap-1.5"
              style={{ backgroundColor: C.greenSoft }}
            >
              <View
                style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: C.green }}
              />
              <Text style={{ color: C.green }} className="text-xs font-semibold" numberOfLines={1}>
                Live
              </Text>
            </View>
            <Text style={{ color: C.text3 }} className="text-xs" numberOfLines={1}>
              MCP Protocol {protocolVersion} · {tools.length} tools
            </Text>
          </View>

          <Text
            className="font-bold mb-3"
            style={{
              fontSize: isDesktop ? 48 : 32,
              lineHeight: isDesktop ? 56 : 38,
              color: C.text,
            }}
          >
            Inverio MCP
          </Text>
          <Text
            style={{
              color: C.text2,
              fontSize: isDesktop ? 18 : 16,
              lineHeight: isDesktop ? 28 : 24,
            }}
            className="mb-6"
          >
            Connect AI agents to Inverio's startup evaluation and due diligence infrastructure.
            Score, verify, and conduct DD on startups across 15 dimensions — directly from
            Claude (desktop or web), ChatGPT, or any MCP-compatible client.
          </Text>
        </Animated.View>

        {/* Endpoint URL */}
        <Animated.View entering={FadeInDown.delay(100).duration(400)}>
          <Text
            style={{ color: C.text3, letterSpacing: 1.5 }}
            className="text-xs font-bold mb-2"
            numberOfLines={1}
          >
            ENDPOINT
          </Text>
          <View
            className="flex-row items-center rounded-xl px-4 py-3 mb-7"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            <Text
              style={{ color: C.text, fontFamily: 'monospace', flex: 1 }}
              className="text-sm"
              selectable
              numberOfLines={1}
            >
              {MCP_ENDPOINT}
            </Text>
            <CopyButton value={MCP_ENDPOINT} />
          </View>
        </Animated.View>

        {/* Auth callout */}
        <Animated.View entering={FadeInDown.delay(150).duration(400)}>
          <View
            className="rounded-xl p-4 mb-7 flex-row items-start gap-3"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            <View
              className="w-10 h-10 rounded-lg items-center justify-center"
              style={{ backgroundColor: C.amberSoft }}
            >
              <Key size={18} color={C.amber} />
            </View>
            <View style={{ flex: 1 }}>
              <Text
                style={{ color: C.text }}
                className="text-sm font-semibold mb-1"
                numberOfLines={1}
              >
                API key required
              </Text>
              <Text style={{ color: C.text2 }} className="text-sm leading-5 mb-3">
                Sign in to Inverio and create an API key from Settings → API Keys. Keys start
                with <Text style={{ fontFamily: 'monospace' }}>inv_</Text> and are scoped per
                user.
              </Text>
              <AnimatedPressable
                onPress={() => {
                  hapticLight();
                  router.push('/settings');
                }}
                className="flex-row items-center self-start gap-1.5 px-3 py-2 rounded-md"
                style={{ backgroundColor: C.brand }}
                accessibilityRole="button"
                accessibilityLabel="Open settings to create API key"
              >
                <Text style={{ color: '#0a1628' }} className="text-sm font-bold" numberOfLines={1}>
                  Get an API key
                </Text>
                <ArrowRight size={14} color="#0a1628" />
              </AnimatedPressable>
              <Text style={{ color: C.text3 }} className="text-xs leading-5 mt-3">
                Or — MCP clients with OAuth support can sign in via{' '}
                <Text style={{ color: C.brand, fontFamily: 'monospace' }}>
                  https://mcp.inverio.ai/oauth/authorize
                </Text>
                . Discover endpoints at{' '}
                <Text style={{ color: C.brand, fontFamily: 'monospace' }}>
                  /.well-known/oauth-authorization-server
                </Text>
                .
              </Text>
            </View>
          </View>
        </Animated.View>

        {/* Tools list */}
        <Animated.View entering={FadeInDown.delay(200).duration(400)}>
          <View className="flex-row items-center gap-2 mb-3">
            <Zap size={16} color={C.text} />
            <Text style={{ color: C.text }} className="text-base font-bold" numberOfLines={1}>
              Available tools
            </Text>
            <Text style={{ color: C.text3 }} className="text-xs" numberOfLines={1}>
              · {tools.length}
            </Text>
          </View>
          <View
            className="rounded-xl overflow-hidden mb-2"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            {tools.map((tool, idx) => (
              <AnimatedPressable
                key={tool.name}
                onPress={() => {
                  hapticLight();
                  router.push(`/mcp/tools/${tool.name}`);
                }}
                className="px-4 py-3"
                style={{
                  borderBottomWidth: idx === tools.length - 1 ? 0 : 1,
                  borderBottomColor: C.border,
                }}
                accessibilityRole="link"
                accessibilityLabel={`Open ${tool.name} documentation`}
              >
                <View className="flex-row items-start gap-3 flex-wrap mb-1">
                  <Text
                    style={{ color: C.brand, fontFamily: 'monospace' }}
                    className="text-sm font-semibold"
                    numberOfLines={1}
                  >
                    {tool.name}
                  </Text>
                  <Text
                    style={{ color: C.text2, flex: 1, minWidth: 200 }}
                    className="text-sm leading-5"
                  >
                    {tool.description}
                  </Text>
                  <ChevronRight size={14} color={C.text3} />
                </View>
                {tool.usage ? (
                  <Text
                    style={{ color: C.text3, paddingLeft: 0 }}
                    className="text-xs leading-5 mt-0.5"
                  >
                    {tool.usage}
                  </Text>
                ) : null}
              </AnimatedPressable>
            ))}
          </View>
          <Text style={{ color: C.text3 }} className="text-xs leading-5 mb-7">
            Tap a tool for full documentation — input schema, example payloads, errors, and side-effect notes.
          </Text>
        </Animated.View>

        {/* Resources list */}
        <Animated.View entering={FadeInDown.delay(220).duration(400)}>
          <View className="flex-row items-center gap-2 mb-3 mt-7">
            <Code size={16} color={C.text} />
            <Text style={{ color: C.text }} className="text-base font-bold" numberOfLines={1}>
              Available resources
            </Text>
            <Text style={{ color: C.text3 }} className="text-xs" numberOfLines={1}>
              · {RESOURCES.length}
            </Text>
          </View>
          <Text style={{ color: C.text2 }} className="text-sm leading-5 mb-3">
            Resources are passive context the agent can pull at conversation start, instead
            of calling tools every turn. Read with{' '}
            <Text style={{ fontFamily: 'monospace' }}>resources/read</Text> on the URI.
          </Text>
          <View
            className="rounded-xl overflow-hidden mb-7"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            {RESOURCES.map((res, idx) => (
              <View
                key={res.uri}
                className="px-4 py-3"
                style={{
                  borderBottomWidth: idx === RESOURCES.length - 1 ? 0 : 1,
                  borderBottomColor: C.border,
                }}
              >
                <View className="flex-row items-center gap-2 mb-1 flex-wrap">
                  <Text
                    style={{ color: C.brand, fontFamily: 'monospace' }}
                    className="text-xs font-semibold"
                    selectable
                  >
                    {res.uri}
                  </Text>
                  <View
                    className="px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor:
                        res.scope === 'public'
                          ? C.greenSoft
                          : res.scope === 'user'
                          ? C.brandSoft
                          : C.amberSoft,
                    }}
                  >
                    <Text
                      style={{
                        color:
                          res.scope === 'public'
                            ? C.green
                            : res.scope === 'user'
                            ? C.brand
                            : C.amber,
                        fontSize: 10,
                      }}
                      className="font-bold uppercase"
                      numberOfLines={1}
                    >
                      {res.scope}
                    </Text>
                  </View>
                </View>
                <Text
                  style={{ color: C.text }}
                  className="text-sm font-semibold mb-0.5"
                  numberOfLines={1}
                >
                  {res.name}
                </Text>
                <Text style={{ color: C.text2 }} className="text-xs leading-5">
                  {res.description}
                </Text>
              </View>
            ))}
          </View>
        </Animated.View>

        {/* Client configuration */}
        <Animated.View entering={FadeInDown.delay(250).duration(400)}>
          <View className="flex-row items-center gap-2 mb-3">
            <Terminal size={16} color={C.text} />
            <Text style={{ color: C.text }} className="text-base font-bold" numberOfLines={1}>
              Client configuration
            </Text>
          </View>
          <Text style={{ color: C.text2 }} className="text-sm leading-5 mb-3">
            Paste into your AI client's MCP configuration. Replace{' '}
            <Text style={{ fontFamily: 'monospace' }}>{'<your-api-key>'}</Text> with your generated
            key, or use Sign in with Inverio if your client supports OAuth.
          </Text>

          <ConfigSnippet client="Claude Desktop" config={claudeDesktopConfig} />
          <ConfigSnippet client="Claude.ai (web Connectors)" config={claudeWebConfig} />
          <ConfigSnippet client="ChatGPT (desktop / Plus)" config={chatgptConfig} />

          {/* Developer IDE integrations — secondary */}
          <View className="mt-6 mb-2">
            <Text
              style={{ color: C.text3, letterSpacing: 1.5 }}
              className="text-xs font-bold"
              numberOfLines={1}
            >
              FOR DEVELOPERS
            </Text>
          </View>
          <ConfigSnippet client="Cursor" config={cursorConfig} />
          <ConfigSnippet client="Cline" config={clineConfig} />
          <ConfigSnippet client="Windsurf" config={windsurfConfig} />

          {/* Bring your own client — Dynamic Client Registration (RFC 7591) */}
          <View
            className="rounded-xl p-5 mt-4"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            <View className="flex-row items-center gap-2 mb-2">
              <Code size={16} color={C.brand} />
              <Text
                style={{ color: C.text }}
                className="text-base font-bold"
                numberOfLines={1}
              >
                Bring your own client (Dynamic Client Registration)
              </Text>
            </View>
            <Text style={{ color: C.text2 }} className="text-sm leading-5 mb-3">
              Building a new MCP client? Self-register at{' '}
              <Text style={{ color: C.brand }} className="font-semibold">
                POST https://mcp.inverio.ai/oauth/register
              </Text>{' '}
              per RFC 7591 — receive a client_id (and optional client_secret) instantly. No
              waitlist, no manual onboarding.
            </Text>
            <View
              className="rounded-md p-3"
              style={{
                backgroundColor: 'rgba(0,0,0,0.4)',
                borderWidth: 1,
                borderColor: C.border,
              }}
            >
              <Text
                style={{
                  color: C.text2,
                  fontFamily: Platform.select({ ios: 'Menlo', default: 'monospace' }),
                }}
                className="text-xs leading-5"
              >
                curl -X POST https://mcp.inverio.ai/oauth/register \{'\n'}
                {'  '}-H "content-type: application/json" \{'\n'}
                {'  '}-d '{`{`}{'\n'}
                {'    '}"client_name": "My Agent",{'\n'}
                {'    '}"redirect_uris": ["http://localhost:3000/cb"],{'\n'}
                {'    '}"token_endpoint_auth_method": "none"{'\n'}
                {'  '}{`}`}'
              </Text>
            </View>
            <Text style={{ color: C.text3 }} className="text-xs leading-5 mt-3">
              Rate-limited to 5 registrations / IP / day. Public (PKCE-only) and confidential
              (client_secret_basic) clients both supported.
            </Text>
          </View>
        </Animated.View>

        {/* Quick start — agent prompt example */}
        <Animated.View entering={FadeInDown.delay(300).duration(400)}>
          <View className="flex-row items-center gap-2 mb-3 mt-7">
            <Zap size={16} color={C.text} />
            <Text style={{ color: C.text }} className="text-base font-bold" numberOfLines={1}>
              Your first call
            </Text>
          </View>
          <Text style={{ color: C.text2 }} className="text-sm leading-5 mb-3">
            Once configured, prompt your agent. Examples that work out of the box:
          </Text>
          <View
            className="rounded-xl overflow-hidden mb-3"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            {[
              {
                role: 'Investor agent',
                prompt:
                  '"Use Inverio to score the startup with ID abc-123 and tell me the top 3 risks."',
              },
              {
                role: 'Founder agent',
                prompt:
                  '"Check my data room readiness and list the documents I\'m missing for fundraising."',
              },
              {
                role: 'Daily briefing',
                prompt:
                  '"Pull my Inverio daily briefing — new submissions, score changes, and portfolio alerts since yesterday."',
              },
            ].map((ex, i, arr) => (
              <View
                key={ex.role}
                className="px-4 py-3"
                style={{
                  borderBottomWidth: i === arr.length - 1 ? 0 : 1,
                  borderBottomColor: C.border,
                }}
              >
                <Text
                  style={{ color: C.brand }}
                  className="text-xs font-bold mb-1"
                  numberOfLines={1}
                >
                  {ex.role}
                </Text>
                <Text style={{ color: C.text2 }} className="text-sm leading-5">
                  {ex.prompt}
                </Text>
              </View>
            ))}
          </View>
          <Text style={{ color: C.text3 }} className="text-xs leading-5">
            For programmatic use, any MCP-compatible client SDK (Python, TypeScript, Rust) works
            against the Streamable HTTP transport at the endpoint above.
          </Text>
        </Animated.View>

        {/* Footer note */}
        <Animated.View entering={FadeIn.delay(400).duration(500)}>
          <View
            className="flex-row items-center gap-2 mt-8 px-4 py-3 rounded-xl flex-wrap"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            <Shield size={16} color={C.text3} />
            <Text style={{ color: C.text3, flex: 1, minWidth: 200 }} className="text-xs leading-5">
              EU-hosted infrastructure. EU AI Act limited-risk classification. Per-key rate
              limiting and usage metering.
            </Text>
            <AnimatedPressable
              onPress={() => {
                hapticLight();
                router.push('/changelog');
              }}
              className="flex-row items-center gap-1"
              accessibilityRole="link"
              accessibilityLabel="Open changelog"
            >
              <Text
                style={{ color: C.brand }}
                className="text-xs font-semibold"
                numberOfLines={1}
              >
                Changelog
              </Text>
              <ExternalLink size={12} color={C.brand} />
            </AnimatedPressable>
            <Text style={{ color: C.text3 }} className="text-xs">
              ·
            </Text>
            <AnimatedPressable
              onPress={() => {
                hapticLight();
                if (Platform.OS === 'web') {
                  window.open(
                    'https://github.com/adikouadio/inverio-app/issues',
                    '_blank',
                  );
                }
              }}
              className="flex-row items-center gap-1"
              accessibilityRole="link"
              accessibilityLabel="View status / report issue"
            >
              <Text
                style={{ color: C.brand }}
                className="text-xs font-semibold"
                numberOfLines={1}
              >
                Status
              </Text>
              <ExternalLink size={12} color={C.brand} />
            </AnimatedPressable>
            <Text style={{ color: C.text3 }} className="text-xs">
              ·
            </Text>
            <AnimatedPressable
              onPress={() => {
                hapticLight();
                if (Platform.OS === 'web') {
                  window.location.href = 'https://inverio.ai';
                } else {
                  router.push('/');
                }
              }}
              className="flex-row items-center gap-1"
              accessibilityRole="link"
              accessibilityLabel="Visit inverio.ai"
            >
              <Text style={{ color: C.brand }} className="text-xs font-semibold" numberOfLines={1}>
                inverio.ai
              </Text>
              <ExternalLink size={12} color={C.brand} />
            </AnimatedPressable>
          </View>
        </Animated.View>
      </View>
    </PublicLayout>
  );
}
